// PDF export - %100 Türkçe karakter garantisi
// A3/A4 seçenekli, otomatik sütun genişliği (autoTable), sayfa bölmeli
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import '../fonts/Roboto-Medium';

const FONT_SIZE = '12px';
const TITLE_FONT_SIZE = '18px';
const CELL_PADDING = '6px 10px';
const HEADER_PADDING = '8px 10px';
const LINE_HEIGHT = '1.4';

// Sayfa boyutları (mm)
const PAGE_SIZES = {
  a4: { landscape: { w: 297, h: 210 }, portrait: { w: 210, h: 297 } },
  a3: { landscape: { w: 420, h: 297 }, portrait: { w: 297, h: 420 } },
};

const hexToRgb = (hex) => {
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex || '');
  return m ? [parseInt(m[1], 16), parseInt(m[2], 16), parseInt(m[3], 16)] : [59, 130, 246];
};

/**
 * HTML elementini PDF'e dönüştür
 */
export const exportElementToPDF = async (element, filename, orientation = 'landscape', pageSize = 'a4') => {
  try {
    const canvas = await html2canvas(element, {
      scale: 2, backgroundColor: '#ffffff', logging: false, useCORS: true, allowTaint: true
    });
    const imgData = canvas.toDataURL('image/png');
    const pdf = new jsPDF(orientation, 'mm', pageSize);
    const dims = PAGE_SIZES[pageSize]?.[orientation] || PAGE_SIZES.a4.landscape;
    const margin = 8;
    const imgWidth = dims.w - margin * 2;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;
    let heightLeft = imgHeight;
    let position = margin;
    pdf.addImage(imgData, 'PNG', margin, position, imgWidth, Math.min(imgHeight, dims.h - margin * 2));
    heightLeft -= (dims.h - margin * 2);
    while (heightLeft > 0) {
      position = heightLeft - imgHeight + margin;
      pdf.addPage();
      pdf.addImage(imgData, 'PNG', margin, position, imgWidth, imgHeight);
      heightLeft -= (dims.h - margin * 2);
    }
    pdf.save(filename);
    return true;
  } catch (error) {
    console.error('PDF export hatası:', error);
    throw error;
  }
};

/**
 * Tek sayfa HTML tablosu oluştur
 */
const buildPageHTML = (headers, rows, headerColor, hasTotalRow, isLastPage, title, pageInfo, subtitle) => {
  const page = document.createElement('div');
  page.style.backgroundColor = 'white';
  page.style.fontFamily = 'Arial, Helvetica, sans-serif';
  page.style.padding = '0';

  // Başlık
  if (title) {
    const titleEl = document.createElement('div');
    titleEl.style.marginBottom = '8px';

    const h1 = document.createElement('h1');
    h1.textContent = title;
    h1.style.fontSize = TITLE_FONT_SIZE;
    h1.style.margin = '0 0 4px 0';
    h1.style.color = '#1f2937';
    titleEl.appendChild(h1);

    if (subtitle) {
      const sub = document.createElement('p');
      sub.textContent = subtitle;
      sub.style.fontSize = '12px';
      sub.style.margin = '0 0 2px 0';
      sub.style.color = '#4b5563';
      titleEl.appendChild(sub);
    }

    if (pageInfo) {
      const pi = document.createElement('p');
      pi.textContent = pageInfo;
      pi.style.fontSize = '11px';
      pi.style.margin = '0';
      pi.style.color = '#6b7280';
      titleEl.appendChild(pi);
    }

    page.appendChild(titleEl);
  } else if (pageInfo) {
    const pi = document.createElement('p');
    pi.textContent = pageInfo;
    pi.style.fontSize = '12px';
    pi.style.fontWeight = 'bold';
    pi.style.margin = '0 0 6px 0';
    pi.style.color = '#374151';
    page.appendChild(pi);
  }

  // Tablo
  const table = document.createElement('table');
  table.style.width = '100%';
  table.style.borderCollapse = 'collapse';
  table.style.fontSize = FONT_SIZE;
  table.style.lineHeight = LINE_HEIGHT;
  table.style.tableLayout = 'auto';

  const thead = document.createElement('thead');
  const headerRow = document.createElement('tr');
  headers.forEach(h => {
    const th = document.createElement('th');
    th.textContent = h;
    th.style.backgroundColor = headerColor;
    th.style.color = 'white';
    th.style.padding = HEADER_PADDING;
    th.style.border = '1px solid #d1d5db';
    th.style.textAlign = 'left';
    th.style.fontWeight = 'bold';
    th.style.fontSize = FONT_SIZE;
    headerRow.appendChild(th);
  });
  thead.appendChild(headerRow);
  table.appendChild(thead);

  const tbody = document.createElement('tbody');
  rows.forEach((row, idx) => {
    const tr = document.createElement('tr');
    const isTotal = hasTotalRow && isLastPage && idx === rows.length - 1;
    tr.style.backgroundColor = isTotal ? '#1e40af' : (idx % 2 === 0 ? '#f9fafb' : 'white');

    row.forEach(cell => {
      const td = document.createElement('td');
      td.textContent = cell != null ? String(cell) : '-';
      td.style.padding = CELL_PADDING;
      td.style.border = '1px solid #d1d5db';
      td.style.fontSize = FONT_SIZE;
      td.style.lineHeight = LINE_HEIGHT;
      td.style.wordBreak = 'break-word';
      if (isTotal) { td.style.color = 'white'; td.style.fontWeight = 'bold'; }
      tr.appendChild(td);
    });
    tbody.appendChild(tr);
  });
  table.appendChild(tbody);
  page.appendChild(table);
  return page;
};

/**
 * Tablo verilerini PDF'e dönüştür - autoTable ile otomatik sütun genişliği
 * Çok sayıda sütun olduğunda A3 yatayda bile düzgün sığar.
 */
export const exportTableToPDF = async (headers, data, title, filename, options = {}) => {
  const {
    subtitle = '',
    hasTotalRow = false,
    headerColor = '#3b82f6',
    orientation = 'landscape',
    pageSize = 'a4',
    cellStyler = null, // (rowIndex, colIndex, value) => { fillColor:[r,g,b], textColor:[r,g,b], fontStyle, halign } | null
  } = options;

  const dims = PAGE_SIZES[pageSize]?.[orientation] || PAGE_SIZES.a4.landscape;
  const pdf = new jsPDF(orientation, 'mm', pageSize);

  // RobotoMedium fontunu kullan (Türkçe karakter desteği)
  try {
    pdf.setFont('RobotoMedium', 'normal');
  } catch (e) {
    pdf.setFont('helvetica', 'normal');
  }

  const margin = 8;
  const headerRgb = hexToRgb(headerColor);

  // Başlık + alt başlık + tarih bandı
  const dateStr = new Date().toLocaleDateString('tr-TR', { year: 'numeric', month: 'long', day: 'numeric' });
  let topY = margin + 2;

  if (title) {
    pdf.setFontSize(14);
    pdf.setTextColor(31, 41, 55);
    pdf.text(title, margin, topY + 4);
    topY += 7;
  }
  if (subtitle) {
    pdf.setFontSize(10);
    pdf.setTextColor(75, 85, 99);
    pdf.text(subtitle, margin, topY + 4);
    topY += 5;
  }
  pdf.setFontSize(9);
  pdf.setTextColor(107, 114, 128);
  pdf.text(`${dateStr} | ${pageSize.toUpperCase()} ${orientation === 'landscape' ? 'Yatay' : 'Dikey'}`, margin, topY + 4);
  topY += 6;

  // Sütun sayısına göre otomatik font boyutu (çok sayıda sütun varsa küçült)
  const colCount = headers.length;
  let fontSize = 8;
  if (colCount > 25) fontSize = 6;
  else if (colCount > 18) fontSize = 6.5;
  else if (colCount > 12) fontSize = 7;
  else if (colCount > 8) fontSize = 7.5;

  // Son satır toplam ise farklı stil
  const totalRowIndex = hasTotalRow ? data.length - 1 : -1;

  autoTable(pdf, {
    head: [headers],
    body: data.map(row => row.map(cell => (cell === null || cell === undefined) ? '-' : String(cell))),
    startY: topY,
    margin: { left: margin, right: margin, top: margin, bottom: margin + 6 },
    styles: {
      font: 'RobotoMedium',
      fontSize: fontSize,
      cellPadding: 1.5,
      overflow: 'linebreak',
      valign: 'middle',
      lineColor: [209, 213, 219],
      lineWidth: 0.1,
      textColor: [17, 24, 39],
    },
    headStyles: {
      font: 'RobotoMedium',
      fontStyle: 'normal',
      fillColor: headerRgb,
      textColor: [255, 255, 255],
      fontSize: fontSize,
      cellPadding: 2,
      halign: 'left',
      valign: 'middle',
      lineWidth: 0.1,
    },
    bodyStyles: {
      font: 'RobotoMedium',
    },
    alternateRowStyles: {
      fillColor: [249, 250, 251],
    },
    didParseCell: (data) => {
      // Toplam satırı için özel stil
      if (data.section === 'body' && data.row.index === totalRowIndex) {
        data.cell.styles.fillColor = [30, 64, 175];
        data.cell.styles.textColor = [255, 255, 255];
        data.cell.styles.fontStyle = 'bold';
      }
      // Hücre bazlı renklendirme (örn. etiket/durum renkleri)
      if (cellStyler && data.section === 'body') {
        const st = cellStyler(data.row.index, data.column.index, data.cell.raw);
        if (st) Object.assign(data.cell.styles, st);
      }
    },
    didDrawPage: (dataArg) => {
      // Sayfa numarası footer
      const pageCount = pdf.internal.getNumberOfPages();
      const pageNo = dataArg.pageNumber;
      pdf.setFont('RobotoMedium', 'normal');
      pdf.setFontSize(8);
      pdf.setTextColor(107, 114, 128);
      pdf.text(
        `Sayfa ${pageNo} / ${pageCount}`,
        dims.w - margin,
        dims.h - 3,
        { align: 'right' }
      );
    },
    showHead: 'everyPage',
    tableWidth: 'auto',
  });

  pdf.save(filename);
  return true;
};

/**
 * Metin PDF export
 */
export const exportTextToPDF = async (title, content, meta, filename) => {
  const tempDiv = document.createElement('div');
  tempDiv.style.position = 'absolute';
  tempDiv.style.left = '-9999px';
  tempDiv.style.width = '700px';
  tempDiv.style.backgroundColor = 'white';
  tempDiv.style.padding = '40px';
  tempDiv.style.fontFamily = 'Arial, Helvetica, sans-serif';

  const titleEl = document.createElement('h1');
  titleEl.textContent = title;
  titleEl.style.fontSize = '20px';
  titleEl.style.marginBottom = '10px';
  titleEl.style.color = '#1f2937';
  tempDiv.appendChild(titleEl);

  if (meta) {
    const metaEl = document.createElement('p');
    metaEl.textContent = meta;
    metaEl.style.fontSize = '12px';
    metaEl.style.marginBottom = '20px';
    metaEl.style.color = '#6b7280';
    tempDiv.appendChild(metaEl);
  }

  const contentEl = document.createElement('div');
  contentEl.textContent = content;
  contentEl.style.fontSize = '14px';
  contentEl.style.lineHeight = '1.7';
  contentEl.style.color = '#374151';
  contentEl.style.whiteSpace = 'pre-wrap';
  tempDiv.appendChild(contentEl);

  document.body.appendChild(tempDiv);
  try {
    await exportElementToPDF(tempDiv, filename, 'portrait');
    return true;
  } finally {
    document.body.removeChild(tempDiv);
  }
};

/**
 * Gruplu tablo PDF export
 */
export const exportGroupedTableToPDF = async (groups, filename, mainTitle, pageSize = 'a4') => {
  const dims = PAGE_SIZES[pageSize]?.portrait || PAGE_SIZES.a4.portrait;
  const margin = 8;
  const pxPerMm = 3.78;
  const containerWidth = Math.round((dims.w - margin * 2) * pxPerMm);

  const container = document.createElement('div');
  container.style.position = 'absolute';
  container.style.left = '-9999px';
  container.style.width = containerWidth + 'px';
  container.style.backgroundColor = 'white';
  container.style.fontFamily = 'Arial, Helvetica, sans-serif';
  document.body.appendChild(container);

  try {
    const pdf = new jsPDF('portrait', 'mm', pageSize);
    const usableW = dims.w - margin * 2;
    const usableH = dims.h - margin * 2;
    let isFirstPage = true;

    for (let gIdx = 0; gIdx < groups.length; gIdx++) {
      const group = groups[gIdx];
      while (container.firstChild) container.removeChild(container.firstChild);

      const wrapper = document.createElement('div');
      wrapper.style.padding = '0';

      if (gIdx === 0) {
        const titleEl = document.createElement('h1');
        titleEl.textContent = mainTitle;
        titleEl.style.fontSize = TITLE_FONT_SIZE;
        titleEl.style.margin = '0 0 4px 0';
        titleEl.style.color = '#1f2937';
        wrapper.appendChild(titleEl);

        const dateEl = document.createElement('p');
        dateEl.textContent = `Tarih: ${new Date().toLocaleDateString('tr-TR', { year: 'numeric', month: 'long', day: 'numeric' })}`;
        dateEl.style.fontSize = '11px';
        dateEl.style.margin = '0 0 12px 0';
        dateEl.style.color = '#6b7280';
        wrapper.appendChild(dateEl);
      }

      const groupTitle = document.createElement('h2');
      groupTitle.textContent = group.title;
      groupTitle.style.fontSize = '15px';
      groupTitle.style.fontWeight = 'bold';
      groupTitle.style.margin = '0 0 8px 0';
      groupTitle.style.color = '#1f2937';
      wrapper.appendChild(groupTitle);

      const tablePage = buildPageHTML(group.headers, group.data, group.headerColor || '#3b82f6', group.hasTotalRow, true, null, null, null);
      wrapper.appendChild(tablePage);
      container.appendChild(wrapper);

      const canvas = await html2canvas(container, {
        scale: 2, backgroundColor: '#ffffff', logging: false, useCORS: true, allowTaint: true
      });

      if (!isFirstPage) pdf.addPage();
      isFirstPage = false;

      const imgData = canvas.toDataURL('image/png');
      const imgW = usableW;
      const imgH = (canvas.height * imgW) / canvas.width;

      pdf.addImage(imgData, 'PNG', margin, margin, imgW, Math.min(imgH, usableH));
    }

    pdf.save(filename);
    return true;
  } finally {
    document.body.removeChild(container);
  }
};

export default { exportElementToPDF, exportTableToPDF, exportTextToPDF, exportGroupedTableToPDF };
